import { useEffect, useState } from 'react';
import { endpoints } from '../../../api';
import type { Person, SelfProfile, UserAvatar } from '../../../api/people';
import { useAccountLocale } from '../../../i18n';
import { useStudioArtwork } from '../../studio/use-studio-artwork';

export interface PeopleData {
  self: SelfProfile | null;
  persons: Person[];
  variants: UserAvatar[];
  loading: boolean;
  error: boolean;
  reload(): void;
  characters: Array<{ id: number; name: string; image: string | null; sex: string | null; collections: number[] }>;
  charactersError: boolean;
}
/** «Вы», варианты, близкие (аккаунт, docs/03-api.md §7.2) и персонажи каталога — один загрузчик для ряда «Кто в кадре». */
export function usePeopleData(withCharacters = true): PeopleData {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ revision: number; self: SelfProfile | null; persons: Person[]; variants: UserAvatar[]; error: boolean }>({
    revision: -1,
    self: null,
    persons: [],
    variants: [],
    error: false,
  });
  const locale = useAccountLocale() === 'ru' ? 'ru' : 'en';
  const artwork = useStudioArtwork(locale, withCharacters ? revision : -1);
  useEffect(() => {
    let active = true;
    void Promise.allSettled([endpoints.people.self(), endpoints.people.list(), endpoints.people.avatars()]).then(([self, list, own]) => {
      if (!active) return;
      setState({
        revision,
        self: self.status === 'fulfilled' ? self.value : null,
        persons: list.status === 'fulfilled' ? list.value.items : [],
        variants: own.status === 'fulfilled' ? own.value.items : [],
        error: self.status === 'rejected' || list.status === 'rejected',
      });
    });
    const sync = () => setRevision(value => value + 1);
    document.addEventListener('bot:me', sync);
    document.addEventListener('bot:avatars', sync);
    return () => {
      active = false;
      document.removeEventListener('bot:me', sync);
      document.removeEventListener('bot:avatars', sync);
    };
  }, [revision]);
  return {
    self: state.self,
    persons: state.persons,
    variants: state.variants,
    loading: state.revision !== revision,
    error: state.error,
    reload: () => setRevision(value => value + 1),
    characters: withCharacters
      ? (artwork.data?.avatars ?? []).map(card => ({
          id: card.id,
          name: card.full_name || card.name,
          image: card.image_url,
          sex: card.sex,
          collections: card.collection_ids,
        }))
      : [],
    charactersError: withCharacters && artwork.error && !artwork.data,
  };
}
